import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, PencilSimple, Link, Tag, Play, Gift, UserCircle, Eye, EyeSlash, DotsSixVertical } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Md } from '@/components/Md';
import { InlineEdit } from '@/components/DraggableList';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { resourceIcon } from '@/lib/resourceIcons';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;
const TYPES = [
  { value: 'link', label: 'Site / Link', icon: Link },
  { value: 'social', label: 'Rede social', icon: UserCircle },
  { value: 'video', label: 'Vídeo', icon: Play },
  { value: 'discount', label: 'Código / Oferta', icon: Gift },
];
const PROGRAMS = [
  { value: 'global', label: 'Global (todas)' },
  { value: 'gravida', label: 'Gravidez' },
  { value: 'pos_parto', label: 'Pós-parto' },
  { value: 'introducao_alimentar', label: 'Introdução alimentar' },
  { value: 'emagrecimento', label: 'Emagrecimento' },
  { value: 'pre_concecao', label: 'Pré-conceção' },
  { value: 'geral', label: 'Genérico' },
];
const TYPE_ICONS = { link: Link, social: UserCircle, video: Play, discount: Gift };
const TYPE_COLORS = { link: 'bg-blue-50 text-blue-600', social: 'bg-purple-50 text-purple-600', video: 'bg-red-50 text-red-600', discount: 'bg-amber-50 text-amber-700' };

export default function RecomendacoesPage() {
  useRequireAdmin();
  const confirmDelete = useConfirmDelete();
  const [items, setItems] = useState([]);
  const [typeCategories, setTypeCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: '', type: 'link', url: '', content: '', description: '', programs: ['global'], type_category: '', active: true });
  const [filterProg, setFilterProg] = useState('');
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const fetch = useCallback(async () => {
    try {
      const params = filterProg ? `?program=${filterProg}` : '';
      const { data } = await axios.get(`${API}/api/recommendations${params}`);
      setItems(data);
    } catch {}
  }, [filterProg]);
  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => { (async () => { try { const { data } = await axios.get(`${API}/api/resource-type-categories`); setTypeCategories(data); } catch {} })(); }, []);
  const catById = (id) => typeCategories.find(c => c.id === id);

  const toggleProgram = (p) => {
    setForm(prev => {
      const progs = prev.programs.includes(p) ? prev.programs.filter(x => x !== p) : [...prev.programs, p];
      return { ...prev, programs: progs.length ? progs : ['global'] };
    });
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Título obrigatório'); return; }
    if (!form.type_category) { toast.error('Escolhe uma categoria de tipo'); return; }
    try {
      if (editId) { await axios.put(`${API}/api/recommendations/${editId}`, form); }
      else { await axios.post(`${API}/api/recommendations`, form); }
      toast.success('Recurso guardado'); setShowForm(false); setEditId(null); fetch();
    } catch { toast.error('Erro'); }
  };

  const del = async (id) => {
    const item = items.find(i => i.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o recurso "${item?.title || ''}".` });
    if (!ok) return;
    await axios.delete(`${API}/api/recommendations/${id}`); toast.success('Eliminado'); fetch();
  };
  const toggleActive = async (item) => { await axios.put(`${API}/api/recommendations/${item.id}`, { active: !item.active }); fetch(); };
  const renameRec = async (id, title) => { await axios.put(`${API}/api/recommendations/${id}`, { title }); fetch(); };

  // Drag reorder
  const handleDragStart = (e, idx) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e, idx) => { e.preventDefault(); setOverIdx(idx); };
  const handleDrop = async (e, targetIdx) => {
    e.preventDefault(); setOverIdx(null);
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); return; }
    const arr = [...items];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(targetIdx > dragIdx ? targetIdx - 1 : targetIdx, 0, moved);
    setItems(arr);
    setDragIdx(null);
    // Persist order
    try { await axios.put(`${API}/api/recommendations/reorder`, { ids: arr.map(i => i.id) }); } catch { toast.error('Erro ao reordenar'); }
  };
  const handleDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  const resetForm = () => setForm({ title: '', type: 'link', url: '', content: '', description: '', programs: ['global'], type_category: typeCategories[0]?.id || '', active: true });

  return (
    <div className="fade-in" data-testid="recomendacoes-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Recomendações</h2>
          <p className="text-xs text-brand-muted mt-0.5">Biblioteca de recursos para as clientes</p>
        </div>
        <button onClick={() => { resetForm(); setEditId(null); setShowForm(true); }} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="add-rec-btn">
          <Plus size={16} /> Novo recurso
        </button>
      </div>

      <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1">
        <button onClick={() => setFilterProg('')} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${!filterProg ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Todos</button>
        {PROGRAMS.map(p => (
          <button key={p.value} onClick={() => setFilterProg(filterProg === p.value ? '' : p.value)} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${filterProg === p.value ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>
            {p.label}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 bg-white border border-brand-border rounded-2xl">
          <Tag size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
          <p className="text-brand-muted text-sm">Sem recomendações.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item, idx) => {
            const cat = catById(item.type_category);
            const Icon = cat ? resourceIcon(cat.icon) : (TYPE_ICONS[item.type] || Link);
            const colors = TYPE_COLORS[item.type] || 'bg-brand-bg text-brand-muted';
            return (
              <div key={item.id}
                draggable onDragStart={e => handleDragStart(e, idx)} onDragEnd={handleDragEnd}
                onDragOver={e => handleDragOver(e, idx)} onDrop={e => handleDrop(e, idx)}
                className={`bg-white border border-brand-border rounded-2xl p-4 transition-all ${!item.active ? 'opacity-50' : ''} ${dragIdx === idx ? 'opacity-40' : ''} ${overIdx === idx && dragIdx !== idx ? 'ring-2 ring-brand-green/40 bg-brand-green/5' : ''}`}
                data-testid={`rec-${item.id}`}>
                <div className="flex items-start gap-3">
                  <div className="cursor-grab active:cursor-grabbing text-brand-placeholder hover:text-brand-green pt-1 flex-shrink-0"><DotsSixVertical size={14} /></div>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${colors}`}>
                    <Icon size={18} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <InlineEdit value={item.title} onSave={v => renameRec(item.id, v)} className="text-sm font-medium text-brand-text" testId={`rec-title-${item.id}`} />
                    {item.description && <Md as="p" className="text-xs text-brand-muted mt-0.5">{item.description}</Md>}
                    {item.url && <p className="text-[10px] text-brand-green truncate mt-0.5">{item.url}</p>}
                    {item.type === 'discount' && item.content && (
                      <div className="mt-1 inline-flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
                        <span className="text-xs font-mono font-semibold text-amber-700">{item.content}</span>
                      </div>
                    )}
                    <div className="flex gap-1 mt-1.5 flex-wrap items-center">
                      {cat && <span className="text-[8px] bg-brand-green/10 text-brand-green px-1.5 py-0.5 rounded-full font-medium">{cat.name}</span>}
                      {(item.programs || []).map(p => (
                        <span key={p} className="text-[8px] bg-brand-bg text-brand-muted px-1.5 py-0.5 rounded-full">{PROGRAMS.find(pr => pr.value === p)?.label || p}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => toggleActive(item)} className="p-1 text-brand-placeholder hover:text-brand-green" title={item.active ? 'Desativar' : 'Ativar'}>
                      {item.active ? <Eye size={14} /> : <EyeSlash size={14} />}
                    </button>
                    <button onClick={() => { setEditId(item.id); setForm({ title: item.title, type: item.type, url: item.url || '', content: item.content || '', description: item.description || '', programs: item.programs || ['global'], type_category: item.type_category || '', active: item.active }); setShowForm(true); }} className="p-1 text-brand-placeholder hover:text-brand-green"><PencilSimple size={14} /></button>
                    <button onClick={() => del(item.id)} className="p-1 text-brand-placeholder hover:text-red-500"><Trash size={14} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editId ? 'Editar recurso' : 'Novo recurso'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Título</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Nome do recurso" className="rounded-xl border-brand-border" data-testid="rec-title" /></div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Tipo</Label>
              <div className="flex gap-1.5 flex-wrap">
                {TYPES.map(t => (
                  <button key={t.value} type="button" onClick={() => setForm({ ...form, type: t.value })}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${form.type === t.value ? 'border-brand-green bg-brand-green/5 text-brand-green' : 'border-brand-border text-brand-muted'}`}>
                    <t.icon size={12} /> {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Categoria de tipo <span className="text-red-500">*</span></Label>
              <div className="flex gap-1.5 flex-wrap" data-testid="rec-type-category">
                {typeCategories.length === 0 && <p className="text-xs text-brand-muted">Sem categorias. Cria nas Definições.</p>}
                {typeCategories.map(c => {
                  const CIcon = resourceIcon(c.icon);
                  return (
                    <button key={c.id} type="button" onClick={() => setForm({ ...form, type_category: c.id })}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${form.type_category === c.id ? 'border-brand-green bg-brand-green/5 text-brand-green' : 'border-brand-border text-brand-muted'}`}
                      data-testid={`rec-cat-${c.id}`}>
                      <CIcon size={12} /> {c.name}
                    </button>
                  );
                })}
              </div>
            </div>
            {form.type !== 'discount' && (
              <div><Label className="text-xs text-brand-muted mb-1 block">URL</Label><Input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://..." className="rounded-xl border-brand-border" /></div>
            )}
            {form.type === 'discount' && (
              <div><Label className="text-xs text-brand-muted mb-1 block">Código de desconto</Label><Input value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="NUTRI20" className="rounded-xl border-brand-border font-mono" /></div>
            )}
            <div><Label className="text-xs text-brand-muted mb-1 block">Descrição (opcional)</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Descrição curta..." className="rounded-xl border-brand-border" rows={2} /></div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Visibilidade (programas)</Label>
              <div className="flex gap-1.5 flex-wrap">
                {PROGRAMS.map(p => (
                  <button key={p.value} type="button" onClick={() => toggleProgram(p.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium ${form.programs.includes(p.value) ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-rec-btn">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
